import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useSlobozip } from '@/lib/useSlobozip';
import { DUR, EASE_OUT_QUINT } from '@/lib/motion';
import type { ArchiveListEntry } from '@/lib/wasm';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MiB
const MAX_ARCHIVE_BYTES = 20 * 1024 * 1024; // 20 MiB for extract input

const SAMPLE_TEXT = `Lossless compression replaces repeats with references. The longer
the input, the more repeats appear, the smaller the output gets.
LZSS is one of the simplest schemes that work: a sliding window
remembers what it has seen, and every time the encoder spots a
repeat in the window, it writes a back-reference instead of the
literal bytes. Try this paragraph. Try a paragraph that repeats.
Try a paragraph that repeats. Try a paragraph that repeats.`;

type Mode = 'text' | 'file';
type Op = 'compress' | 'extract';

type ExtractedEntry = ArchiveListEntry & { data: Uint8Array };
type ExtractState =
  | { kind: 'idle' }
  | { kind: 'extracting'; name: string; size: number }
  | { kind: 'listed'; name: string; size: number; entries: ExtractedEntry[] }
  | { kind: 'error'; message: string };

type State =
  | { kind: 'idle' }
  | { kind: 'compressing'; stage: number; originalSize: number; name: string }
  | {
      kind: 'result';
      originalSize: number;
      compressedSize: number;
      archive: Uint8Array;
      name: string;
    }
  | { kind: 'error'; message: string };

const STAGES = ['scanning.', 'writing.', 'sealed.'];

/**
 * Squeeze — Figure 1.
 *
 * The interactive demo, staged like a moment. Idle drop zone → engine
 * compression beat (3 microcopy stages, ~700ms total) → result panel
 * slides in with original/compressed/ratio + .slbz download.
 *
 * Backed by the real WASM module via useSlobozip(). No mock data.
 */
export function Squeeze() {
  const wasm = useSlobozip();
  const [op, setOp] = useState<Op>('compress');
  const [mode, setMode] = useState<Mode>('text');
  const [text, setText] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [extractState, setExtractState] = useState<ExtractState>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const archiveInputRef = useRef<HTMLInputElement>(null);

  const reset = () => setState({ kind: 'idle' });
  const resetExtract = () => setExtractState({ kind: 'idle' });

  const onExtractFile = async (file: File) => {
    if (wasm.status !== 'ready') return;
    if (file.size > MAX_ARCHIVE_BYTES) {
      setExtractState({
        kind: 'error',
        message: 'Archive exceeds 20 MiB. Try a smaller one.',
      });
      return;
    }
    setExtractState({ kind: 'extracting', name: file.name, size: file.size });
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const list = wasm.api.listArchive(bytes);
      const extracted = wasm.api.extractArchive(bytes);
      const byName = new Map(extracted.map((e) => [e.name, e.data]));
      const merged: ExtractedEntry[] = list.map((l) => ({
        ...l,
        data: byName.get(l.name) ?? new Uint8Array(),
      }));
      await beat(180);
      setExtractState({
        kind: 'listed',
        name: file.name,
        size: file.size,
        entries: merged,
      });
    } catch (err) {
      setExtractState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Extraction failed',
      });
    }
  };

  const onDropArchive = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await onExtractFile(file);
  };

  const runCompression = async (data: Uint8Array, name: string) => {
    if (wasm.status !== 'ready') return;

    // Stage the microcopy beats over ~700ms regardless of how fast the
    // actual compression is. Makes the moment feel like something
    // happened instead of a flash. Real work happens at stage 0.
    setState({ kind: 'compressing', stage: 0, originalSize: data.length, name });

    let archive: Uint8Array;
    try {
      archive = wasm.api.createArchive([{ name, data }]);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Compression failed',
      });
      return;
    }

    await beat(280);
    setState({ kind: 'compressing', stage: 1, originalSize: data.length, name });
    await beat(280);
    setState({ kind: 'compressing', stage: 2, originalSize: data.length, name });
    await beat(220);

    setState({
      kind: 'result',
      originalSize: data.length,
      compressedSize: archive.length,
      archive,
      name,
    });
  };

  const onCompressText = () => {
    const t = text || SAMPLE_TEXT;
    if (!t) return;
    const bytes = new TextEncoder().encode(t);
    if (bytes.length > MAX_BYTES) {
      setState({ kind: 'error', message: 'Text exceeds 5 MiB. Try less.' });
      return;
    }
    void runCompression(bytes, 'input.txt');
  };

  const onCompressFile = async (file: File) => {
    if (file.size > MAX_BYTES) {
      setState({ kind: 'error', message: 'File exceeds 5 MiB. Try a smaller one.' });
      return;
    }
    const buf = await file.arrayBuffer();
    void runCompression(new Uint8Array(buf), file.name);
  };

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await onCompressFile(file);
  };

  return (
    <section
      id="squeeze"
      style={{
        padding: '160px 0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Atmosphere — soft warm spill behind the demo card.
       *  Studio key-light on a product shot. Subtle by intent. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '52%',
          transform: 'translate(-50%, -50%)',
          width: 720,
          height: 480,
          background:
            'radial-gradient(ellipse at center, var(--accent-glow), transparent 70%)',
          opacity: 0.5,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div className="container-x" style={{ position: 'relative', zIndex: 1 }}>
        {/* Eyebrow + heading */}
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--accent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          figure 1
        </span>
        <h2
          className="t-heading"
          style={{
            marginTop: 16,
            fontSize: 'var(--type-xl)',
            maxWidth: '18ch',
          }}
        >
          {op === 'compress' ? 'Drop a file. Watch it shrink.' : 'Drop a .slbz. Unpack it.'}
        </h2>
        <p
          style={{
            marginTop: 20,
            fontSize: 'var(--type-md)',
            color: 'var(--text-2)',
            maxWidth: '60ch',
            lineHeight: 1.55,
          }}
        >
          {op === 'compress'
            ? 'The same WASM module that ships with SloboZip runs in your browser tab. No upload, no server. Compression happens locally, then the archive comes back as a download.'
            : 'Same module, the other direction. Drop a .slbz, the header is parsed, the entries are decompressed, every CRC is checked. Save them one at a time.'}
        </p>

        {/* Op toggle — compress vs extract */}
        <OpToggle
          op={op}
          setOp={(next) => {
            setOp(next);
            setState({ kind: 'idle' });
            setExtractState({ kind: 'idle' });
          }}
          disabled={wasm.status !== 'ready'}
        />

        {/* Card */}
        <motion.div
          layout
          transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
          style={{
            marginTop: 24,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            overflow: 'hidden',
            position: 'relative',
            maxWidth: 720,
            marginInline: 'auto',
          }}
        >
          {op === 'extract' ? (
            <ExtractFlow
              state={extractState}
              onDrop={onDropArchive}
              onPick={() => archiveInputRef.current?.click()}
              onReset={resetExtract}
              wasmReady={wasm.status === 'ready'}
            />
          ) : (
          <AnimatePresence mode="wait" initial={false}>
            {state.kind === 'idle' && (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: DUR.base, ease: EASE_OUT_QUINT }}
              >
                <Tabs mode={mode} setMode={setMode} disabled={wasm.status !== 'ready'} />

                {mode === 'text' ? (
                  <TextPanel
                    text={text}
                    setText={setText}
                    onSubmit={onCompressText}
                    disabled={wasm.status !== 'ready'}
                  />
                ) : (
                  <FilePanel
                    onDrop={onDrop}
                    onPick={() => fileInputRef.current?.click()}
                    disabled={wasm.status !== 'ready'}
                  />
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onCompressFile(f);
                  }}
                />

                <WasmStatus status={wasm.status} />
              </motion.div>
            )}

            {state.kind === 'compressing' && (
              <motion.div
                key="compressing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: DUR.base }}
              >
                <Engine stage={state.stage} originalSize={state.originalSize} />
              </motion.div>
            )}

            {state.kind === 'result' && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
              >
                <Result
                  originalSize={state.originalSize}
                  compressedSize={state.compressedSize}
                  archive={state.archive}
                  name={state.name}
                  onReset={reset}
                />
              </motion.div>
            )}

            {state.kind === 'error' && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ padding: 56, textAlign: 'center' }}
              >
                <p style={{ color: 'var(--accent)', marginBottom: 16 }}>{state.message}</p>
                <button
                  onClick={reset}
                  className="t-mono"
                  style={{
                    fontSize: 'var(--type-xs)',
                    color: 'var(--text-2)',
                    letterSpacing: 'var(--tracking-mono-caps)',
                    textTransform: 'uppercase',
                  }}
                >
                  ← try again
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          )}
        </motion.div>

        <input
          ref={archiveInputRef}
          type="file"
          accept=".slbz,application/octet-stream"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onExtractFile(f);
          }}
        />
      </div>
    </section>
  );
}

/* ── Sub-components ────────────────────────────────────────────────── */

function Tabs({
  mode,
  setMode,
  disabled,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {(['text', 'file'] as Mode[]).map((m) => {
        const on = m === mode;
        return (
          <button
            key={m}
            onClick={() => !disabled && setMode(m)}
            disabled={disabled}
            className="t-mono"
            style={{
              flex: 1,
              padding: '18px 0',
              fontSize: 'var(--type-xs)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              color: on ? 'var(--text)' : 'var(--text-3)',
              borderBottom: on ? '1px solid var(--accent)' : '1px solid transparent',
              transition:
                'color var(--dur-fast) var(--ease-out-expo), border-color var(--dur-fast) var(--ease-out-expo)',
            }}
          >
            {m}
          </button>
        );
      })}
    </div>
  );
}

function TextPanel({
  text,
  setText,
  onSubmit,
  disabled,
}: {
  text: string;
  setText: (t: string) => void;
  onSubmit: () => void;
  disabled: boolean;
}) {
  return (
    <div style={{ padding: 32 }}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Try repeating patterns. e.g. "${SAMPLE_TEXT.slice(0, 80)}..."`}
        disabled={disabled}
        rows={8}
        style={{
          width: '100%',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--type-sm)',
          padding: 18,
          background: 'var(--bg-warm)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          color: 'var(--text)',
          resize: 'vertical',
          lineHeight: 1.6,
          outline: 'none',
        }}
      />
      <button
        onClick={onSubmit}
        disabled={disabled}
        style={{
          marginTop: 20,
          width: '100%',
          padding: '16px 24px',
          fontSize: 'var(--type-sm)',
          fontWeight: 600,
          color: 'var(--bg)',
          background: disabled ? 'var(--text-3)' : 'var(--text)',
          borderRadius: 10,
          letterSpacing: '-0.01em',
          transition: 'background var(--dur-fast) var(--ease-out-expo)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.background = 'var(--accent)';
        }}
        onMouseLeave={(e) => {
          if (!disabled) e.currentTarget.style.background = 'var(--text)';
        }}
      >
        Compress
      </button>
    </div>
  );
}

function FilePanel({
  onDrop,
  onPick,
  disabled,
}: {
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onPick: () => void;
  disabled: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div style={{ padding: 32 }}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          if (!disabled) onDrop(e);
        }}
        onClick={() => !disabled && onPick()}
        style={{
          padding: '64px 24px',
          textAlign: 'center',
          background: over ? 'var(--accent-bg)' : 'var(--bg-warm)',
          border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--border-strong)'}`,
          borderRadius: 12,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition:
            'background var(--dur-fast) var(--ease-out-expo), border-color var(--dur-fast) var(--ease-out-expo)',
        }}
      >
        <p
          style={{
            fontSize: 'var(--type-md)',
            fontWeight: 500,
            color: 'var(--text)',
          }}
        >
          Drop a file, or click to browse
        </p>
        <p
          className="t-mono"
          style={{
            marginTop: 10,
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
          }}
        >
          up to 5 MiB · stays in your browser
        </p>
      </div>
    </div>
  );
}

function Engine({ stage, originalSize }: { stage: number; originalSize: number }) {
  return (
    <div
      style={{
        padding: '80px 32px',
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'var(--accent-bg)',
            animation: 'pulseRing 1.6s var(--ease-in-out-cubic) infinite',
          }}
        />
        <span
          style={{
            position: 'absolute',
            inset: 16,
            borderRadius: '50%',
            background: 'var(--accent)',
            animation: 'pulseCore 1.6s var(--ease-in-out-cubic) infinite',
          }}
        />
      </div>

      <p
        className="t-mono"
        style={{
          marginTop: 32,
          fontSize: 'var(--type-xs)',
          color: 'var(--text-3)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
          minHeight: '1.4em',
        }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={stage}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: DUR.fast }}
            style={{ display: 'inline-block' }}
          >
            {STAGES[stage]}
          </motion.span>
        </AnimatePresence>
      </p>

      <p
        className="t-mono"
        style={{
          marginTop: 8,
          fontSize: 'var(--type-xs)',
          color: 'var(--text-4)',
          letterSpacing: 'var(--tracking-mono-caps)',
        }}
      >
        {formatBytes(originalSize)} in
      </p>

      <style>{`
        @keyframes pulseRing {
          0%   { transform: scale(0.85); opacity: 0.7; }
          50%  { transform: scale(1.05); opacity: 0.3; }
          100% { transform: scale(0.85); opacity: 0.7; }
        }
        @keyframes pulseCore {
          0%   { transform: scale(1); opacity: 1; }
          50%  { transform: scale(0.92); opacity: 0.85; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function Result({
  originalSize,
  compressedSize,
  archive,
  name,
  onReset,
}: {
  originalSize: number;
  compressedSize: number;
  archive: Uint8Array;
  name: string;
  onReset: () => void;
}) {
  const ratio = Math.max(0, Math.round((1 - compressedSize / originalSize) * 100));
  const orig = useCount(originalSize);
  const comp = useCount(compressedSize);
  const pct = useCount(ratio);

  const download = () => {
    // Wrap the underlying buffer slice into a fresh ArrayBuffer-typed view
    // so TS's BlobPart type accepts it without DOM lib quirks.
    const blob = new Blob([archive.slice().buffer as ArrayBuffer], {
      type: 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = baseName(name) + '.slbz';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          {name} → {baseName(name)}.slbz
        </span>
        <button
          onClick={onReset}
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            transition: 'color var(--dur-fast) var(--ease-out-expo)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-3)')}
        >
          ← try again
        </button>
      </div>

      {/* Big numbers row */}
      <div
        style={{
          marginTop: 28,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <p
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--text-3)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            before
          </p>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--type-lg)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: 'var(--text-2)',
            }}
          >
            {formatBytes(orig)}
          </p>
        </div>

        <div style={{ flex: 1, position: 'relative', height: 1, margin: '0 8px' }}>
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--border)',
            }}
          />
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: DUR.slow, delay: 0.2, ease: EASE_OUT_QUINT }}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--accent)',
              transformOrigin: 'left',
              width: `${100 - ratio}%`,
            }}
          />
        </div>

        <div style={{ textAlign: 'right' }}>
          <p
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--accent)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            after
          </p>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--type-lg)',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--text)',
            }}
          >
            {formatBytes(comp)}
          </p>
        </div>
      </div>

      {/* Ratio sentence */}
      <p
        style={{
          marginTop: 32,
          fontSize: 'var(--type-md)',
          color: 'var(--text-2)',
          textAlign: 'center',
          lineHeight: 1.4,
        }}
      >
        {pct > 0 ? (
          <>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{pct}% smaller</span>{' '}
            after compression{compressedSize < originalSize ? '' : ' (overhead exceeded gains)'}.
          </>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>
            no compressible patterns found, archive is larger than the source
          </span>
        )}
      </p>

      {/* Download */}
      <button
        onClick={download}
        style={{
          marginTop: 28,
          width: '100%',
          padding: '16px 24px',
          fontSize: 'var(--type-sm)',
          fontWeight: 600,
          color: 'var(--bg)',
          background: 'var(--accent)',
          borderRadius: 10,
          letterSpacing: '-0.01em',
          transition: 'background var(--dur-fast) var(--ease-out-expo)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-hover)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent)')}
      >
        Download .slbz ({formatBytes(compressedSize)})
      </button>
    </div>
  );
}

function WasmStatus({ status }: { status: 'idle' | 'loading' | 'ready' | 'error' }) {
  let text = '';
  if (status === 'idle' || status === 'loading') text = 'wasm engine. loading';
  if (status === 'ready') text = 'wasm engine. ready';
  if (status === 'error')
    text = 'wasm engine. failed to load (build the wasm module first)';

  return (
    <div
      className="t-mono"
      style={{
        padding: '14px 32px',
        fontSize: 11,
        color: status === 'error' ? 'var(--accent)' : 'var(--text-3)',
        letterSpacing: 'var(--tracking-mono-caps)',
        textTransform: 'uppercase',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-warm)',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          marginRight: 8,
          background: status === 'ready' ? 'var(--accent)' : 'var(--text-4)',
          animation: status === 'loading' ? 'pulseDot 1.4s ease infinite' : 'none',
        }}
      />
      {text}
      <style>{`
        @keyframes pulseDot { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </div>
  );
}

/* ── Op toggle ─────────────────────────────────────────────────────── */

function OpToggle({
  op,
  setOp,
  disabled,
}: {
  op: Op;
  setOp: (o: Op) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        marginTop: 56,
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: 'var(--bg-warm)',
        border: '1px solid var(--border)',
        borderRadius: 999,
      }}
    >
      {(['compress', 'extract'] as Op[]).map((o) => {
        const on = op === o;
        return (
          <button
            key={o}
            onClick={() => !disabled && !on && setOp(o)}
            disabled={disabled}
            className="t-mono"
            style={{
              padding: '10px 22px',
              minHeight: 44,
              fontSize: 'var(--type-xs)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              color: on ? 'var(--bg)' : 'var(--text-2)',
              background: on ? 'var(--text)' : 'transparent',
              borderRadius: 999,
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition:
                'background var(--dur-fast) var(--ease-out-expo), color var(--dur-fast) var(--ease-out-expo)',
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

/* ── Extract flow ──────────────────────────────────────────────────── */

function ExtractFlow({
  state,
  onDrop,
  onPick,
  onReset,
  wasmReady,
}: {
  state: ExtractState;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onPick: () => void;
  onReset: () => void;
  wasmReady: boolean;
}) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      {state.kind === 'idle' && (
        <motion.div
          key="ex-idle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DUR.base, ease: EASE_OUT_QUINT }}
        >
          <ArchiveDrop onDrop={onDrop} onPick={onPick} disabled={!wasmReady} />
          <WasmStatus status={wasmReady ? 'ready' : 'loading'} />
        </motion.div>
      )}

      {state.kind === 'extracting' && (
        <motion.div
          key="ex-extracting"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DUR.base }}
        >
          <Engine stage={1} originalSize={state.size} />
          <p
            className="t-mono"
            style={{
              textAlign: 'center',
              paddingBottom: 32,
              marginTop: -32,
              fontSize: 'var(--type-xs)',
              color: 'var(--text-4)',
              letterSpacing: 'var(--tracking-mono-caps)',
            }}
          >
            opening · verifying · unpacking
          </p>
        </motion.div>
      )}

      {state.kind === 'listed' && (
        <motion.div
          key="ex-listed"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
        >
          <EntryList
            archiveName={state.name}
            archiveSize={state.size}
            entries={state.entries}
            onReset={onReset}
          />
        </motion.div>
      )}

      {state.kind === 'error' && (
        <motion.div
          key="ex-error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ padding: 56, textAlign: 'center' }}
        >
          <p style={{ color: 'var(--accent)', marginBottom: 16 }}>{state.message}</p>
          <button
            onClick={onReset}
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--text-2)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              minHeight: 44,
              padding: '0 16px',
            }}
          >
            ← try again
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ArchiveDrop({
  onDrop,
  onPick,
  disabled,
}: {
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onPick: () => void;
  disabled: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div style={{ padding: 'clamp(20px, 4vw, 32px)' }}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          if (!disabled) onDrop(e);
        }}
        onClick={() => !disabled && onPick()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            onPick();
          }
        }}
        style={{
          padding: 'clamp(48px, 10vw, 64px) 24px',
          textAlign: 'center',
          background: over ? 'var(--accent-bg)' : 'var(--bg-warm)',
          border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--border-strong)'}`,
          borderRadius: 12,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition:
            'background var(--dur-fast) var(--ease-out-expo), border-color var(--dur-fast) var(--ease-out-expo)',
          minHeight: 44,
        }}
      >
        <p
          style={{
            fontSize: 'var(--type-md)',
            fontWeight: 500,
            color: 'var(--text)',
          }}
        >
          Drop a .slbz, or tap to browse
        </p>
        <p
          className="t-mono"
          style={{
            marginTop: 10,
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
          }}
        >
          up to 20 MiB · stays in your browser
        </p>
      </div>
    </div>
  );
}

function EntryList({
  archiveName,
  archiveSize,
  entries,
  onReset,
}: {
  archiveName: string;
  archiveSize: number;
  entries: ExtractedEntry[];
  onReset: () => void;
}) {
  const totalOrig = entries.reduce((s, e) => s + e.original, 0);
  const totalComp = entries.reduce((s, e) => s + e.compressed, 0);

  const downloadOne = (e: ExtractedEntry) => {
    const blob = new Blob([e.data.slice().buffer as ArrayBuffer], {
      type: 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = e.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 'clamp(24px, 4vw, 40px)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            wordBreak: 'break-all',
          }}
        >
          {archiveName} · {formatBytes(archiveSize)}
        </span>
        <button
          onClick={onReset}
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            minHeight: 44,
            padding: '0 8px',
          }}
        >
          ← drop another
        </button>
      </div>

      <p
        style={{
          marginTop: 18,
          fontSize: 'var(--type-md)',
          color: 'var(--text-2)',
          lineHeight: 1.5,
        }}
      >
        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{entries.length}</span>{' '}
        {entries.length === 1 ? 'entry' : 'entries'} ·{' '}
        <span style={{ color: 'var(--text)' }}>{formatBytes(totalComp)}</span> →{' '}
        <span style={{ color: 'var(--text)' }}>{formatBytes(totalOrig)}</span> decompressed.
        Header parsed, every CRC verified.
      </p>

      <ul
        style={{
          marginTop: 24,
          listStyle: 'none',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxHeight: 360,
          overflowY: 'auto',
        }}
      >
        {entries.map((e, i) => (
          <li
            key={`${e.name}-${i}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background: 'var(--bg-warm)',
              border: '1px solid var(--border)',
              borderRadius: 10,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <p
                className="t-mono"
                style={{
                  fontSize: 'var(--type-sm)',
                  color: 'var(--text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={e.name}
              >
                {e.name}
              </p>
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  className="t-mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--text-3)',
                    letterSpacing: 'var(--tracking-mono-caps)',
                  }}
                >
                  {formatBytes(e.compressed)} → {formatBytes(e.original)}
                </span>
                <span
                  className="t-mono"
                  title="CRC-32 matched after decompression"
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: 'var(--accent-bg)',
                    color: 'var(--accent)',
                    letterSpacing: 'var(--tracking-mono-caps)',
                    textTransform: 'uppercase',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="2.5,6.5 5,9 9.5,3.5" />
                  </svg>
                  crc ok
                </span>
              </div>
            </div>
            <button
              onClick={() => downloadOne(e)}
              className="t-mono"
              style={{
                minHeight: 44,
                padding: '0 16px',
                fontSize: 'var(--type-xs)',
                fontWeight: 600,
                color: 'var(--bg)',
                background: 'var(--accent)',
                borderRadius: 8,
                letterSpacing: 'var(--tracking-mono-caps)',
                textTransform: 'uppercase',
                transition: 'background var(--dur-fast) var(--ease-out-expo)',
                cursor: 'pointer',
              }}
              onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--accent-hover)')}
              onMouseLeave={(ev) => (ev.currentTarget.style.background = 'var(--accent)')}
            >
              save
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── helpers ───────────────────────────────────────────────────────── */

function beat(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(2)} MiB`;
}

function baseName(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

function useCount(target: number, durationMs = 700): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 4);
      setV(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return v;
}
